import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import { ENDPOINTS } from 'config/endpoints';
import {
  ActivityKind,
  normalizeTableCellActivityEntity,
  TableCellActivityEntity,
} from 'entities/TableCellActivity';
import {
  FormTableDataActivitiesAddRequest,
  FormTableDataActivitiesAddResponse,
} from 'entities/endpoints/form.table_data.activities.add';
import {
  FormTableDataActivitiesDeleteRequest,
  FormTableDataActivitiesDeleteResponse,
} from 'entities/endpoints/form.table_data.activities.delete';
import {
  FormTableDataActivitiesEditRequest,
  FormTableDataActivitiesEditResponse,
} from 'entities/endpoints/form.table_data.activities.edit';
import {
  FormTableDataActivitiesGetRequest,
  FormTableDataActivitiesGetResponse,
} from 'entities/endpoints/form.table_data.activities.get';
import { ApiRequest } from 'stores/common/ApiRequest';
import { TableCellController } from 'stores/common/TableController';
import { ToggleStateStore } from 'stores/common/ToggleStateStore';
import { ILocalStore } from 'stores/interfaces';
import { EntryText } from 'stores/models';
import { debounce } from 'utils/debounce';
import { validateNotEmpty } from 'utils/validators';

import { ITableColumnModel } from '../TableFactoryHeaderStore';

type PrivateFields =
  | '_currentEntity'
  | '_activities'
  | '_filters'
  | '_activityToDelete'
  | '_activityToEdit'
  | '_addActivity'
  | '_editActivity'
  | '_params';

const SET_FILTER_DEBOUNCE_TIME = 600;

type TableControllerCommentsStoreParams = {
  columns: Map<string, ITableColumnModel>;
};

export class TableControllerCommentsStore implements ILocalStore {
  constructor() {
    makeObservable<TableControllerCommentsStore, PrivateFields>(this, {
      _currentEntity: observable.ref,
      currentEntity: computed,
      handleCommentSelect: action.bound,

      _activities: observable.ref,
      activities: computed,

      handleSaveText: action.bound,

      fetchActivities: action.bound,
      _addActivity: action.bound,
      deleteActivity: action.bound,
      _editActivity: action.bound,

      _activityToEdit: observable.ref,
      handleEditActivity: action.bound,

      _activityToDelete: observable.ref,
      handleDeleteActivity: action.bound,
      handleCancelDelete: action.bound,
      fetchingDelete: computed,

      _filters: observable.ref,
      filters: computed,
      removeFilter: action.bound,
      addFilter: action.bound,

      fetching: computed,
      saveCommentButtonDisabled: computed,

      _params: observable.ref,
      setParams: action.bound,
    });

    const debouncedChangeFilter = debounce(
      this.fetchActivities,
      SET_FILTER_DEBOUNCE_TIME
    );

    this._reactions.push(
      reaction(
        () => this._filters,
        async () => {
          debouncedChangeFilter();
        }
      )
    );
  }

  readonly textModel = new EntryText({
    defaultValue: '',
    validator: validateNotEmpty(),
  });

  private _params: TableControllerCommentsStoreParams | null = null;

  readonly deleteModal = new ToggleStateStore();

  private readonly _getActivitiesRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.activities.get,
    methodType: 'GET',
  });

  private readonly _addActivityRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.activities.add,
    methodType: 'POST',
  });

  private readonly _deleteActivityRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.activities.delete,
    methodType: 'DELETE',
  });

  private readonly _editActivityRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.activities.edit,
    methodType: 'PUT',
  });

  private readonly _reactions: IReactionDisposer[] = [];

  private _currentEntity: TableCellController | null = null;
  private _activities: TableCellActivityEntity[] = [];

  private _activityToEdit: TableCellActivityEntity | null = null;
  private _activityToDelete: TableCellActivityEntity | null = null;

  private _filters: ActivityKind[] = [
    ActivityKind.change,
    ActivityKind.comment,
    ActivityKind.status_change,
  ];

  readonly drawerState = new ToggleStateStore();

  get currentEntity() {
    return this._currentEntity;
  }

  get activities(): TableCellActivityEntity[] {
    return this._activities.map((item) => {
      if (item.edits) {
        item.edits.forEach((activity) => {
          if (!this._params) {
            return;
          }

          const item = this._params.columns.get(activity.columnId);

          if (!item) {
            return;
          }

          activity.label = item.entity.name;
        });
      }

      return item;
    });
  }

  get filters(): ActivityKind[] {
    return this._filters;
  }

  get fetching(): boolean {
    return (
      this._getActivitiesRequest.loading || this._addActivityRequest.loading
    );
  }

  get fetchingDelete(): boolean {
    return this._deleteActivityRequest.loading;
  }

  get saveCommentButtonDisabled(): boolean {
    return this.textModel.value.length === 0;
  }

  setParams(params: TableControllerCommentsStoreParams): void {
    this._params = params;
  }

  async handleSaveText(): Promise<void> {
    if (this._activityToEdit) {
      await this._editActivity();

      return;
    }

    await this._addActivity();
  }

  addFilter(filter: ActivityKind): void {
    this._filters = this._filters.concat(filter);
  }

  removeFilter(filter: ActivityKind): void {
    this._filters = this._filters.filter((item) => item !== filter);
  }

  handleDeleteActivity(entity: TableCellActivityEntity): void {
    this._activityToDelete = entity;
    this.deleteModal.open();
  }

  handleEditActivity(entity: TableCellActivityEntity): void {
    this._activityToEdit = entity;

    if (entity.comment?.value) {
      this.textModel.setValue(entity.comment.value);
    }
  }

  handleCancelDelete(): void {
    this._activityToDelete = null;
    this.deleteModal.close();
  }

  private async _editActivity(): Promise<void> {
    const text = this.textModel.value;

    const rowId = this._currentEntity?.params.parentRowModel?.entity?.rowId;

    if (!rowId || !this._activityToEdit) {
      return;
    }

    const { isError, data } = await this._editActivityRequest.call<
      FormTableDataActivitiesEditResponse,
      FormTableDataActivitiesEditRequest
    >({
      data: {
        filters: this.filters,
        activity_id: this._activityToEdit.id,
        row_id: rowId,
        value: text,
      },
    });

    if (isError || !data) {
      return;
    }

    this.textModel.reset();

    runInAction(() => {
      this._activityToEdit = null;
      this._activities = data.activities.map((activity) =>
        normalizeTableCellActivityEntity(activity)
      );
    });
  }

  private async _addActivity(): Promise<void> {
    const text = this.textModel.value;

    const rowId = this._currentEntity?.params.parentRowModel?.entity?.rowId;

    if (!rowId || !text) {
      return;
    }

    const { isError, data } = await this._addActivityRequest.call<
      FormTableDataActivitiesAddResponse,
      FormTableDataActivitiesAddRequest
    >({
      data: {
        row_id: rowId,
        filters: this._filters,
        type: ActivityKind.comment,
        value: text,
      },
    });

    if (isError || !data) {
      return;
    }

    this.textModel.reset();

    runInAction(() => {
      this._activities = data.activities.map((activity) =>
        normalizeTableCellActivityEntity(activity)
      );
    });
  }

  async deleteActivity(): Promise<void> {
    if (
      !this._currentEntity?.params.parentRowModel?.entity?.rowId ||
      !this._activityToDelete
    ) {
      return;
    }

    const { data, isError } = await this._deleteActivityRequest.call<
      FormTableDataActivitiesDeleteResponse,
      FormTableDataActivitiesDeleteRequest
    >({
      data: {
        activity_id: this._activityToDelete.id,
        row_id: this._currentEntity?.params.parentRowModel.entity?.rowId,
        filters: this.filters,
      },
    });

    this.deleteModal.close();

    if (!data || isError) {
      return;
    }

    runInAction(() => {
      this._activityToEdit = null;
      this.textModel.setValue('');

      this._activities = data.activities.map((activity) =>
        normalizeTableCellActivityEntity(activity)
      );
    });
  }

  async fetchActivities(): Promise<void> {
    if (!this._currentEntity?.params.parentRowModel?.entity?.rowId) {
      return;
    }

    const { data, isError } = await this._getActivitiesRequest.call<
      FormTableDataActivitiesGetResponse,
      FormTableDataActivitiesGetRequest
    >({
      data: {
        row_id: this._currentEntity?.params.parentRowModel.entity?.rowId,
        filters: this.filters,
      },
    });

    if (!data || isError) {
      return;
    }

    runInAction(() => {
      this._activities = data.activities.map((activity) =>
        normalizeTableCellActivityEntity(activity)
      );
    });
  }

  handleCommentSelect(entity: TableCellController): void {
    this._currentEntity = entity;
    this.drawerState.open();
  }

  destroy() {
    this._currentEntity = null;
    this._activities = [];
    this.drawerState.destroy();
    this._addActivityRequest.destroy();
    this._getActivitiesRequest.destroy();
    this._deleteActivityRequest.destroy();
    this._editActivityRequest.destroy();
    this.textModel.destroy();
    this.deleteModal.destroy();
    this._reactions.forEach((reaction) => reaction());
  }
}

export default TableControllerCommentsStore;
